/*
 Copyright (c) 2017 Wells Fargo. 455 Market St., San Francisco, CA 94105 All rights reserved.

 This software is the confidential and proprietary information of Wells Fargo
 bank. ('Confidential Information'). You shall not disclose such Confidential
 Information and shall use it only in accordance with the terms of the license
 agreement you entered into with WellsFargo.

 Author: Thomas Barry
 */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Block from 'wfui-digital-core/es/Block';
import useBrand from 'wfui-digital-core/es/useBrand';
import useDevice from 'wfui-digital-core/es/useDevice';
import useStrings from 'wfui-digital-core/es/useStrings';
import { getClasses } from 'wfui-digital-core/es/lib';
import { fetchNavigationMenuConfig } from '../../lib/store/navigation-menu/actions';
import { showModuleError as _showModuleError } from '../../lib/store/module-error/actions';
import globals from '../../lib/globals/globals';
import useSettings from '../hooks/use-settings';
import StickyFooter from '../sticky-footer/StickyFooter';
import Footer from './footer/Footer';
import PageContent from './PageContent';
import GlobalSearch from './global-search/GlobalSearch';
import LifestyleImage from './lifestyle-image/LifestyleImage';
import SwipeableContainer from './swipeable-container/SwipeableContainer';
import { default as LegacyMasthead } from './masthead/Masthead';
import { default as BimMasthead } from './masthead-bim/Masthead';
import styles from './Page.less';

var Page = function Page(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      customStyleSheet = _ref.customStyleSheet,
      controlBarContent = _ref.controlBarContent,
      controlBarDisabled = _ref.controlBarDisabled,
      lifestyleImagePath = _ref.lifestyleImagePath,
      lifestyleImageClassName = _ref.lifestyleImageClassName,
      masthead = _ref.masthead,
      footer = _ref.footer,
      children = _ref.children;
  var dispatch = useDispatch();
  var brand = useBrand();
  var device = useDevice();

  var _useStrings = useStrings(),
      strings = _useStrings.strings;

  var platform = device.platform;

  var _useSettings = useSettings(),
      useMobileStickyFooter = _useSettings.useMobileStickyFooter,
      useTPBBimUpdates = _useSettings.useTPBBimUpdates;

  var suppressPagePrint = useSelector(function (_ref2) {
    var digitalCore = _ref2.digitalCore;
    return digitalCore.childWindows.suppressPagePrint;
  });
  var fragments = useSelector(function (_ref3) {
    var digitalCore = _ref3.digitalCore;
    return digitalCore.fragments;
  });
  var blockScrolling = useSelector(function (_ref4) {
    var digitalCore = _ref4.digitalCore;
    return digitalCore.scrollBlockers > 0;
  });
  var useWFFonts = useSelector(function (_ref5) {
    var digitalCore = _ref5.digitalCore;
    return digitalCore.useWFFonts;
  });
  var language = useSelector(function (_ref6) {
    var core = _ref6.core;
    return core.language.language;
  });
  var navigationDisabled = useSelector(function (_ref7) {
    var core = _ref7.core;
    return core.navigation.navigationDisabled;
  });
  var navigationMenuOpen = useSelector(function (_ref8) {
    var core = _ref8.core;
    return core.navigationMenu.menuOpen;
  });
  var moduleErrorMessage = useSelector(function (_ref9) {
    var core = _ref9.core;
    return core.moduleError.message;
  });
  var tealiumEnabled = useSelector(function (_ref10) {
    var core = _ref10.core;
    return core.tealium.enabled;
  });
  var tealiumLoaded = useSelector(function (_ref11) {
    var core = _ref11.core;
    return core.tealium.loaded;
  });
  var globalSearchActive = useSelector(function (_ref12) {
    var core = _ref12.core;
    return core.globalSearch.open;
  });
  var footerVisible = useSelector(function (_ref13) {
    var core = _ref13.core;
    return core.page.footerVisible;
  });
  useEffect(function () {
    dispatch(fetchNavigationMenuConfig());
  }, [dispatch, platform]);
  useEffect(function () {
    if (globals().enableLanguageToggle) {
      dispatch(fetchNavigationMenuConfig());
    }
  }, [dispatch, language]);
  useEffect(function () {
    document.body.setAttribute('data-block-scrolling', blockScrolling);
  }, [blockScrolling]);
  useEffect(function () {
    document.body.setAttribute('data-navigation-menu-open', navigationMenuOpen);
  }, [navigationMenuOpen]);
  var classes = ['page', suppressPagePrint && 'noPrint', navigationDisabled && 'navigationDisabled', useWFFonts && 'useWFFonts', useTPBBimUpdates && 'useTPBBimUpdates', globalSearchActive && 'globalSearchActive', useMobileStickyFooter && 'useMobileStickyFooter'];
  var classNames = getClasses({
    styles: styles,
    classes: classes,
    brand: brand,
    device: device,
    className: className
  }); // Consider Tealium "ready" when tmsWaitForAppReady is true or tealium is not
  // enabled or tealium is enabled and tealium is loaded. When tmsWaitForAppReady is
  // true, tealium won't load until the application calls setApplicationReady (SettingsProvider)

  var tealiumReady = tealiumLoaded || globals().tmsWaitForAppReady || !tealiumEnabled;
  var dependenciesReady = strings !== null && (fragments !== null || 'development' === process.env.NODE_ENV) && tealiumReady;
  var Masthead = masthead ? masthead : brand === 'tpb' && !useTPBBimUpdates ? LegacyMasthead : BimMasthead;
  var PageFooter = footer ? footer : Footer;
  return /*#__PURE__*/React.createElement(SwipeableContainer, {
    className: styles.swipeableContainer
  }, /*#__PURE__*/React.createElement(Block, {
    layout: true,
    flex: true,
    vertical: true,
    noShrink: true,
    "data-page-wrapper": ""
  }, /*#__PURE__*/React.createElement(LifestyleImage, {
    path: lifestyleImagePath,
    className: lifestyleImageClassName
  }), /*#__PURE__*/React.createElement(Masthead, {
    controlBarDisabled: controlBarDisabled,
    controlBarContent: dependenciesReady ? controlBarContent : null
  }), /*#__PURE__*/React.createElement(GlobalSearch, null), /*#__PURE__*/React.createElement(Block, {
    layout: true,
    vertical: true,
    flex: true,
    noShrink: true,
    className: classNames,
    "data-page-container": ""
  }, /*#__PURE__*/React.createElement(Block, {
    flex: true,
    layout: true,
    vertical: true,
    center: true
  }, /*#__PURE__*/React.createElement(PageContent, {
    dependenciesReady: dependenciesReady,
    showModuleError: function showModuleError() {
      return dispatch(_showModuleError());
    },
    moduleErrorMessage: moduleErrorMessage
  }, children)), footerVisible && useMobileStickyFooter && /*#__PURE__*/React.createElement(StickyFooter, null), dependenciesReady && !useMobileStickyFooter && /*#__PURE__*/React.createElement(PageFooter, {
    customStyleSheet: customStyleSheet
  }))));
};

export default Page;